/**
 * UK Bank Holidays (England & Wales)
 * Source: gov.uk bank holidays for 2024–2028.
 * Format: YYYY-MM-DD → { name, emoji, tone }
 *
 * `tone` values:
 *   'festive'  → Christmas / New Year / Boxing Day (rose+gold)
 *   'spring'   → Easter, May, Spring bank holidays (emerald)
 *   'summer'   → Summer bank holiday (amber)
 *   'royal'    → King's Birthday / special one-off holidays (violet)
 */
export const UK_BANK_HOLIDAYS = {
  // ═══ 2024 ═══
  '2024-01-01': { name: "New Year's Day", emoji: '🎉', tone: 'festive' },
  '2024-03-29': { name: 'Good Friday',    emoji: '✝️', tone: 'spring' },
  '2024-04-01': { name: 'Easter Monday',  emoji: '🐣', tone: 'spring' },
  '2024-05-06': { name: 'Early May Bank Holiday', emoji: '🌸', tone: 'spring' },
  '2024-05-27': { name: 'Spring Bank Holiday',    emoji: '🌼', tone: 'spring' },
  '2024-08-26': { name: 'Summer Bank Holiday',    emoji: '☀️', tone: 'summer' },
  '2024-12-25': { name: 'Christmas Day',  emoji: '🎄', tone: 'festive' },
  '2024-12-26': { name: 'Boxing Day',     emoji: '🎁', tone: 'festive' },

  // ═══ 2025 ═══
  '2025-01-01': { name: "New Year's Day", emoji: '🎉', tone: 'festive' },
  '2025-04-18': { name: 'Good Friday',    emoji: '✝️', tone: 'spring' },
  '2025-04-21': { name: 'Easter Monday',  emoji: '🐣', tone: 'spring' },
  '2025-05-05': { name: 'Early May Bank Holiday', emoji: '🌸', tone: 'spring' },
  '2025-05-26': { name: 'Spring Bank Holiday',    emoji: '🌼', tone: 'spring' },
  '2025-08-25': { name: 'Summer Bank Holiday',    emoji: '☀️', tone: 'summer' },
  '2025-12-25': { name: 'Christmas Day',  emoji: '🎄', tone: 'festive' },
  '2025-12-26': { name: 'Boxing Day',     emoji: '🎁', tone: 'festive' },

  // ═══ 2026 ═══
  '2026-01-01': { name: "New Year's Day", emoji: '🎉', tone: 'festive' },
  '2026-04-03': { name: 'Good Friday',    emoji: '✝️', tone: 'spring' },
  '2026-04-06': { name: 'Easter Monday',  emoji: '🐣', tone: 'spring' },
  '2026-05-04': { name: 'Early May Bank Holiday', emoji: '🌸', tone: 'spring' },
  '2026-05-25': { name: 'Spring Bank Holiday',    emoji: '🌼', tone: 'spring' },
  '2026-08-31': { name: 'Summer Bank Holiday',    emoji: '☀️', tone: 'summer' },
  '2026-12-25': { name: 'Christmas Day',  emoji: '🎄', tone: 'festive' },
  '2026-12-28': { name: 'Boxing Day (substitute)', emoji: '🎁', tone: 'festive' },

  // ═══ 2027 ═══
  '2027-01-01': { name: "New Year's Day", emoji: '🎉', tone: 'festive' },
  '2027-03-26': { name: 'Good Friday',    emoji: '✝️', tone: 'spring' },
  '2027-03-29': { name: 'Easter Monday',  emoji: '🐣', tone: 'spring' },
  '2027-05-03': { name: 'Early May Bank Holiday', emoji: '🌸', tone: 'spring' },
  '2027-05-31': { name: 'Spring Bank Holiday',    emoji: '🌼', tone: 'spring' },
  '2027-08-30': { name: 'Summer Bank Holiday',    emoji: '☀️', tone: 'summer' },
  '2027-12-27': { name: 'Christmas Day (substitute)', emoji: '🎄', tone: 'festive' },
  '2027-12-28': { name: 'Boxing Day (substitute)',    emoji: '🎁', tone: 'festive' },

  // ═══ 2028 ═══
  '2028-01-03': { name: "New Year's Day (substitute)", emoji: '🎉', tone: 'festive' },
  '2028-04-14': { name: 'Good Friday',    emoji: '✝️', tone: 'spring' },
  '2028-04-17': { name: 'Easter Monday',  emoji: '🐣', tone: 'spring' },
  '2028-05-01': { name: 'Early May Bank Holiday', emoji: '🌸', tone: 'spring' },
  '2028-05-29': { name: 'Spring Bank Holiday',    emoji: '🌼', tone: 'spring' },
  '2028-08-28': { name: 'Summer Bank Holiday',    emoji: '☀️', tone: 'summer' },
  '2028-12-25': { name: 'Christmas Day',  emoji: '🎄', tone: 'festive' },
  '2028-12-26': { name: 'Boxing Day',     emoji: '🎁', tone: 'festive' },
};

/**
 * Tone → tailwind class tokens for calendar rendering
 */
export const HOLIDAY_TONE_STYLES = {
  festive: {
    // Rose + gold Christmas vibe
    cellBorder: 'border-rose-400/40',
    cellBg:     'bg-gradient-to-br from-rose-500/[0.12] via-amber-500/[0.05] to-rose-500/[0.08]',
    cellRing:   'shadow-[inset_0_0_0_1px_rgba(244,63,94,0.18)]',
    chipBg:     'bg-rose-500/25',
    chipText:   'text-rose-100',
    chipBorder: 'border-rose-400/40',
    dot:        'bg-rose-400',
  },
  spring: {
    cellBorder: 'border-emerald-400/35',
    cellBg:     'bg-gradient-to-br from-emerald-500/[0.10] via-teal-500/[0.04] to-emerald-500/[0.06]',
    cellRing:   'shadow-[inset_0_0_0_1px_rgba(52,211,153,0.15)]',
    chipBg:     'bg-emerald-500/25',
    chipText:   'text-emerald-100',
    chipBorder: 'border-emerald-400/40',
    dot:        'bg-emerald-400',
  },
  summer: {
    cellBorder: 'border-amber-400/40',
    cellBg:     'bg-gradient-to-br from-amber-500/[0.12] via-orange-500/[0.05] to-amber-500/[0.06]',
    cellRing:   'shadow-[inset_0_0_0_1px_rgba(251,191,36,0.18)]',
    chipBg:     'bg-amber-500/25',
    chipText:   'text-amber-100',
    chipBorder: 'border-amber-400/40',
    dot:        'bg-amber-400',
  },
  royal: {
    cellBorder: 'border-violet-400/40',
    cellBg:     'bg-gradient-to-br from-violet-500/[0.12] via-fuchsia-500/[0.05] to-violet-500/[0.06]',
    cellRing:   'shadow-[inset_0_0_0_1px_rgba(139,92,246,0.18)]',
    chipBg:     'bg-violet-500/25',
    chipText:   'text-violet-100',
    chipBorder: 'border-violet-400/40',
    dot:        'bg-violet-400',
  },
};

/**
 * Return the UK bank holiday info for a given Date, or null.
 * Accepts Date or 'YYYY-MM-DD' string.
 */
export const getUkHoliday = (date) => {
  if (!date) return null;
  const iso = typeof date === 'string'
    ? date
    : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return UK_BANK_HOLIDAYS[iso] || null;
};

/**
 * All UK holidays for a given calendar month (year, month 0-indexed).
 * Returns an array of { date, name, emoji, tone }.
 */
export const holidaysInMonth = (year, monthIndex) => {
  const prefix = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
  return Object.entries(UK_BANK_HOLIDAYS)
    .filter(([iso]) => iso.startsWith(prefix))
    .map(([iso, meta]) => ({ date: iso, ...meta }));
};
